import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jobsApi, proposalsApi } from '../services/api';
import type { Proposal } from '../types';
import { ProposalTile } from '../components/obros/ProposalTile';
import { StateBadge } from '../components/ui/StateBadge';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { SkeletonCard } from '../components/ui/Skeleton';
import { Skeleton } from '../components/ui/Skeleton';

type FilterKey = 'all' | 'pending' | 'accepted' | 'rejected';

const filters: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'pending', label: 'Pendientes' },
  { key: 'accepted', label: 'Aceptadas' },
  { key: 'rejected', label: 'Rechazadas' },
];

// Calca app/obros/lib/screens/job_proposals_screen.dart
export const JobProposals: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<any>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([jobsApi.get(Number(id)), proposalsApi.list({ job_id: id })])
      .then(([jobRes, proposalsRes]) => {
        setJob(jobRes.data.job);
        setProposals(proposalsRes.data.data ?? proposalsRes.data);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const visible = filter === 'all' ? proposals : proposals.filter((p) => p.status === filter);

  return (
    <div className="bg-surface-dim min-h-full">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Link to={`/jobs/${id}`} className="inline-flex items-center gap-1 text-sm font-medium text-onSurface-mute hover:text-onSurface mb-4">
          <MaterialIcon name="arrow_back" size={16} /> Volver al trabajo
        </Link>

        {/* Contexto del trabajo — para no perder de vista a cuál pertenecen estas propuestas */}
        {loading ? (
          <Skeleton className="h-24 rounded-2xl mb-6" />
        ) : job ? (
          <div className="bg-surface border border-outlineVarWarm rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-1.5">
              <StateBadge status={job.status} />
              {job.is_urgent && <StateBadge status="urgent" />}
            </div>
            <h1 className="font-bold text-onSurface">{job.title}</h1>
            <p className="text-sm text-onSurface-mute mt-0.5">{job.budget_range}</p>
          </div>
        ) : null}

        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-onSurface flex items-center gap-2">
            <MaterialIcon name="groups" size={18} className="text-primary-600" />
            Propuestas
          </h2>
          <span className="text-sm text-onSurface-mute">{proposals.length}</span>
        </div>

        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 mb-4">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap ${
                filter === f.key
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'bg-surface border-outlineVarWarm text-onSurface hover:border-primary-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
            : visible.map((p) => <ProposalTile key={p.id} proposal={p} perspective="client" />)}
        </div>

        {!loading && visible.length === 0 && (
          <p className="text-center text-onSurface-mute py-16">
            {proposals.length === 0 ? 'Todavía no recibiste propuestas para este trabajo.' : 'No hay propuestas con ese filtro.'}
          </p>
        )}
      </div>
    </div>
  );
};
